// Global Instructions Rule Applied!
//
// Configuration → Deal Documents (admin only).
//
// Three cfg tables on one panel, in dependency order:
//   1. Categories  — cfg.document_categories. The coarse grouping (Deal
//      Documents, Pitch Decks, …). Categories OWN types, so a deal file's
//      category is always derived from the type it is classified as.
//   2. Document types — cfg.document_types, each in one category. These are
//      the values the deal Documents tab classifies files with.
//   3. Required documents — cfg.deal_document_requirements. One rule per
//      document a deal must produce, scoped by execution pipeline, deal type,
//      security type and fund. Every scope is a multi-select list: empty
//      means "applies to all", otherwise the deal must match ANY entry
//      (owner instruction 2026-09-02). A rule is satisfied by ANY of its
//      document types at or beyond the required lifecycle.
//
// Two Active switches, two meanings: a requirement's switch hides the rule
// from every deal; a document type's switch only stops it classifying new
// files — the rule stays, the type shows "(inactive)" here and drops out of
// the deal tab's "Satisfied by any of" list.
//
// Chrome copied from configuration/components/AgentGatewayPanel.tsx (the newest
// design-pattern-compliant sibling): FormSection + AntD Table inside it,
// Popconfirm deletes, the standard Tag vocabulary.
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Button, Input, InputNumber, Modal, Popconfirm, Select, Switch, Table, Tag, Typography
} from 'antd'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { PIPELINE_TYPE_DEAL_EXECUTION } from '../../../state_manager/globals.ts'
import {
  LOOKUP_CATEGORY_DEAL_TYPE,
  LOOKUP_CATEGORY_SECURITY_TYPE
} from '../../../state_manager/constants/lookupCategories.ts'
import { useLookups } from '../../global_configs/hooks/useLookups.ts'
import { PipelinesModel } from '../../../state_manager/data_models/pipelines_model.ts'
import { FundsModel } from '../../../state_manager/data_models/funds_model.ts'
import {
  DocumentCategoryAdminRow,
  DocumentConfigAdminData,
  DocumentRequirementAdminRow,
  DocumentTypeAdminRow
} from '../../../state_manager/data_models/document_config_model.ts'
import {
  createDocumentCategory,
  createDocumentRequirement,
  createDocumentType,
  deleteDocumentCategory,
  deleteDocumentRequirement,
  deleteDocumentType,
  DocumentRequirementFields,
  fetchDocumentConfig,
  updateDocumentCategory,
  updateDocumentRequirement,
  updateDocumentType
} from '../services/documentConfigService.ts'
import { CONFIG_PANEL_SCROLL_CLASS } from '../configModules.ts'

const { Text } = Typography

// Mirrors the CHECK constraint on cfg.deal_document_requirements.required_lifecycle
// and public.file_urls_deals.lifecycle. draft < execution < executed.
const LIFECYCLE_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'execution', label: 'Execution' },
  { value: 'executed', label: 'Executed' }
]

const LIFECYCLE_COLORS: Record<string, string> = {
  draft: 'blue',
  execution: 'gold',
  executed: 'green'
}

const ANY = 'Any'

const EMPTY_REQUIREMENT: DocumentRequirementFields = {
  name: '',
  pipeline_ids: [],
  deal_type_ids: [],
  security_type_ids: [],
  fund_ids: [],
  required_lifecycle: 'executed',
  satisfied_by_document_type_ids: [],
  sequence: 0,
  active: true
}

export default function DocumentsPanel(): React.ReactElement {
  const token = () => getAccessToken(store.getState().auth)

  const [data, setData] = useState<DocumentConfigAdminData>({
    categories: [],
    types: [],
    requirements: []
  })
  const [loading, setLoading] = useState(true)
  const [newCategory, setNewCategory] = useState('')
  const [newType, setNewType] = useState('')
  const [newTypeCategory, setNewTypeCategory] = useState<string | null>(null)
  const [editing, setEditing] = useState<DocumentRequirementFields | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const { detailsByCategory } = useLookups()
  const pipelines = useSelector(
    (state: { globalConfigs?: { pipelines?: PipelinesModel[] } }) => state.globalConfigs?.pipelines || []
  )
  const funds = useSelector(
    (state: { globalConfigs?: { funds?: FundsModel[] } }) => state.globalConfigs?.funds || []
  )

  const executionPipelineOptions = useMemo(
    () =>
      pipelines
        .filter((p) => p.type === PIPELINE_TYPE_DEAL_EXECUTION)
        .map((p) => ({ value: p.id as string, label: p.name as string })),
    [pipelines]
  )
  const dealTypeOptions = useMemo(
    () =>
      (detailsByCategory[LOOKUP_CATEGORY_DEAL_TYPE] || []).map((d) => ({ value: d.id, label: d.label })),
    [detailsByCategory]
  )
  const securityTypeOptions = useMemo(
    () =>
      (detailsByCategory[LOOKUP_CATEGORY_SECURITY_TYPE] || []).map((d) => ({
        value: d.id,
        label: d.label
      })),
    [detailsByCategory]
  )
  const fundOptions = useMemo(
    () => funds.map((f) => ({ value: f.id as string, label: f.name as string })),
    [funds]
  )
  const categoryOptions = useMemo(
    () =>
      data.categories.map((c) => ({
        value: c.document_categories__id,
        label: c.document_categories__label || '—'
      })),
    [data.categories]
  )
  // Inactive types cannot be newly picked, but one already on the rule being
  // edited stays visible (marked) so the admin can see and remove it.
  const editingTypeIds = editing?.satisfied_by_document_type_ids
  const typeOptions = useMemo(
    () =>
      data.types
        .filter(
          (t) =>
            t.document_types__active !== false ||
            (editingTypeIds || []).includes(t.document_types__id)
        )
        .map((t) => {
          const base = t.document_categories__label
            ? `${t.document_types__label} · ${t.document_categories__label}`
            : (t.document_types__label as string)
          return {
            value: t.document_types__id,
            label: t.document_types__active === false ? `${base} (inactive)` : base
          }
        }),
    [data.types, editingTypeIds]
  )

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setData(await fetchDocumentConfig(token()))
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load document configuration')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // A write either succeeds and we reload (counts and labels are all derived
  // server-side), or it surfaces the backend's message — including the 409
  // in-use guards, which say exactly what is blocking the delete.
  const run = async (action: () => Promise<void>, failure: string): Promise<boolean> => {
    try {
      await action()
      await load()
      return true
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : failure)
      return false
    }
  }

  // ---- categories ---------------------------------------------------------
  const handleAddCategory = async (): Promise<void> => {
    const label = newCategory.trim()
    if (!label) return
    const nextOrder =
      Math.max(0, ...data.categories.map((c) => c.document_categories__sort_order ?? 0)) + 10
    if (await run(
      () => createDocumentCategory(token(), { label, sort_order: nextOrder }),
      'Failed to add category'
    )) {
      setNewCategory('')
    }
  }

  const categoryColumns = [
    {
      title: 'Category',
      dataIndex: 'document_categories__label',
      render: (v: string, row: DocumentCategoryAdminRow) => (
        <Input
          size='small'
          defaultValue={v || ''}
          onBlur={(e) => {
            const next = e.target.value.trim()
            if (next && next !== v) {
              run(
                () => updateDocumentCategory(token(), row.document_categories__id, { label: next }),
                'Failed to rename category'
              )
            }
          }}
        />
      )
    },
    {
      title: 'Order',
      dataIndex: 'document_categories__sort_order',
      width: 90,
      render: (v: number, row: DocumentCategoryAdminRow) => (
        <InputNumber
          size='small'
          min={0}
          value={v ?? 0}
          onBlur={(e) => {
            const next = Number((e.target as HTMLInputElement).value)
            if (!Number.isNaN(next) && next !== v) {
              run(
                () => updateDocumentCategory(token(), row.document_categories__id, { sort_order: next }),
                'Failed to reorder category'
              )
            }
          }}
        />
      )
    },
    {
      title: 'Types',
      dataIndex: 'document_categories__type_count',
      width: 80,
      render: (v: number) => <Text type='secondary'>{v ?? 0}</Text>
    },
    {
      title: 'Active',
      dataIndex: 'document_categories__active',
      width: 80,
      render: (v: boolean, row: DocumentCategoryAdminRow) => (
        <Switch
          size='small'
          checked={v !== false}
          onChange={(checked) =>
            run(
              () => updateDocumentCategory(token(), row.document_categories__id, { active: checked }),
              'Failed to update category'
            )
          }
        />
      )
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_: unknown, row: DocumentCategoryAdminRow) => (
        <Popconfirm
          title='Delete this category?'
          description='Only possible while no document type still sits in it.'
          okText='Delete'
          okButtonProps={{ danger: true }}
          onConfirm={() =>
            run(
              () => deleteDocumentCategory(token(), row.document_categories__id),
              'Failed to delete category'
            )
          }
        >
          <Button size='small' type='text' danger icon={<DeleteOutlined />} />
        </Popconfirm>
      )
    }
  ]

  // ---- document types -----------------------------------------------------
  const handleAddType = async (): Promise<void> => {
    const label = newType.trim()
    if (!label) return
    const nextOrder = Math.max(0, ...data.types.map((t) => t.document_types__sort_order ?? 0)) + 10
    if (await run(
      () =>
        createDocumentType(token(), {
          label,
          sort_order: nextOrder,
          document_category_id: newTypeCategory
        }),
      'Failed to add document type'
    )) {
      setNewType('')
    }
  }

  const typeColumns = [
    {
      title: 'Document type',
      dataIndex: 'document_types__label',
      render: (v: string, row: DocumentTypeAdminRow) => (
        <Input
          size='small'
          defaultValue={v || ''}
          onBlur={(e) => {
            const next = e.target.value.trim()
            if (next && next !== v) {
              run(
                () => updateDocumentType(token(), row.document_types__id, { label: next }),
                'Failed to rename document type'
              )
            }
          }}
        />
      )
    },
    {
      title: 'Category',
      dataIndex: 'document_category_id',
      width: 220,
      render: (v: string, row: DocumentTypeAdminRow) => (
        <Select
          size='small'
          allowClear
          className='w-full'
          placeholder='Uncategorised'
          value={v ?? undefined}
          options={categoryOptions}
          onChange={(value) =>
            run(
              () =>
                updateDocumentType(token(), row.document_types__id, {
                  document_category_id: value ?? null
                }),
              'Failed to recategorise document type'
            )
          }
        />
      )
    },
    {
      title: 'Order',
      dataIndex: 'document_types__sort_order',
      width: 90,
      render: (v: number, row: DocumentTypeAdminRow) => (
        <InputNumber
          size='small'
          min={0}
          value={v ?? 0}
          onBlur={(e) => {
            const next = Number((e.target as HTMLInputElement).value)
            if (!Number.isNaN(next) && next !== v) {
              run(
                () => updateDocumentType(token(), row.document_types__id, { sort_order: next }),
                'Failed to reorder document type'
              )
            }
          }}
        />
      )
    },
    {
      title: 'In use',
      key: 'usage',
      width: 150,
      render: (_: unknown, row: DocumentTypeAdminRow) => {
        const files = row.document_types__file_count ?? 0
        const reqs = row.document_types__requirement_count ?? 0
        if (!files && !reqs) return <Text type='secondary'>—</Text>
        return (
          <Text type='secondary' style={{ fontSize: 12 }}>
            {files ? `${files} file(s)` : ''}
            {files && reqs ? ' · ' : ''}
            {reqs ? `${reqs} rule(s)` : ''}
          </Text>
        )
      }
    },
    {
      title: 'Active',
      dataIndex: 'document_types__active',
      width: 80,
      render: (v: boolean, row: DocumentTypeAdminRow) => (
        <Switch
          size='small'
          checked={v !== false}
          onChange={(checked) =>
            run(
              () => updateDocumentType(token(), row.document_types__id, { active: checked }),
              'Failed to update document type'
            )
          }
        />
      )
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_: unknown, row: DocumentTypeAdminRow) => (
        <Popconfirm
          title='Delete this document type?'
          description='Only possible while no deal file is classified with it and no rule is satisfied by it.'
          okText='Delete'
          okButtonProps={{ danger: true }}
          onConfirm={() =>
            run(() => deleteDocumentType(token(), row.document_types__id), 'Failed to delete document type')
          }
        >
          <Button size='small' type='text' danger icon={<DeleteOutlined />} />
        </Popconfirm>
      )
    }
  ]

  // ---- requirements -------------------------------------------------------
  const openNewRequirement = (): void => {
    setEditingId(null)
    setEditing({ ...EMPTY_REQUIREMENT })
  }

  const openEditRequirement = (row: DocumentRequirementAdminRow): void => {
    setEditingId(row.deal_document_requirements__id)
    setEditing({
      name: row.deal_document_requirements__name || '',
      pipeline_ids: row.pipeline_ids || [],
      deal_type_ids: row.deal_type_ids || [],
      security_type_ids: row.security_type_ids || [],
      fund_ids: row.fund_ids || [],
      required_lifecycle: row.deal_document_requirements__required_lifecycle || 'executed',
      satisfied_by_document_type_ids: row.satisfied_by_document_type_ids || [],
      sequence: row.deal_document_requirements__sequence ?? 0,
      active: row.deal_document_requirements__active !== false
    })
  }

  const handleSaveRequirement = async (): Promise<void> => {
    if (!editing) return
    if (!(editing.name || '').trim()) {
      showErrorToast('Name is required')
      return
    }
    if (!(editing.satisfied_by_document_type_ids || []).length) {
      showErrorToast('Pick at least one document type that satisfies this requirement')
      return
    }
    setSaving(true)
    const ok = await run(
      () =>
        editingId
          ? updateDocumentRequirement(token(), editingId, editing)
          : createDocumentRequirement(token(), editing),
      'Failed to save requirement'
    )
    setSaving(false)
    if (ok) {
      setEditing(null)
      setEditingId(null)
    }
  }

  // One Tag per scope entry; an empty list is the single "Any" Tag.
  const scopeTags = (labels: string[] | null | undefined, color: string): React.ReactElement =>
    labels && labels.length ? (
      <span className='inline-flex flex-wrap gap-y-1'>
        {labels.map((label) => (
          <Tag key={label} color={color}>
            {label}
          </Tag>
        ))}
      </span>
    ) : (
      <Tag>{ANY}</Tag>
    )

  const requirementColumns = [
    {
      title: 'Requirement',
      dataIndex: 'deal_document_requirements__name',
      width: 210,
      render: (v: string, row: DocumentRequirementAdminRow) => (
        <button
          type='button'
          className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
          onClick={() => openEditRequirement(row)}
        >
          {v || '—'}
        </button>
      )
    },
    {
      title: 'Pipeline',
      dataIndex: 'pipelines__names',
      width: 190,
      render: (v: string[]) => scopeTags(v, 'purple')
    },
    {
      title: 'Deal type',
      dataIndex: 'deal_type__labels',
      width: 150,
      render: (v: string[]) => scopeTags(v, 'blue')
    },
    {
      title: 'Security type',
      dataIndex: 'security_type__labels',
      width: 150,
      render: (v: string[]) => scopeTags(v, 'blue')
    },
    {
      title: 'Fund',
      dataIndex: 'funds__names',
      width: 170,
      render: (v: string[]) => scopeTags(v, 'purple')
    },
    {
      title: 'Satisfied by any of',
      dataIndex: 'satisfied_by_labels',
      width: 320,
      render: (v: string[]) => (
        <span className='text-xs text-gray-400'>{(v || []).join(' · ') || '—'}</span>
      )
    },
    {
      title: 'Needs',
      dataIndex: 'deal_document_requirements__required_lifecycle',
      width: 110,
      render: (v: string) => <Tag color={LIFECYCLE_COLORS[String(v)] || 'default'}>{v}</Tag>
    },
    {
      title: '#',
      dataIndex: 'deal_document_requirements__sequence',
      width: 70,
      render: (v: number) => <Text type='secondary'>{v ?? 0}</Text>
    },
    {
      title: 'Active',
      dataIndex: 'deal_document_requirements__active',
      width: 80,
      render: (v: boolean, row: DocumentRequirementAdminRow) => (
        <Switch
          size='small'
          checked={v !== false}
          onChange={(checked) =>
            run(
              () =>
                updateDocumentRequirement(token(), row.deal_document_requirements__id, {
                  active: checked
                }),
              'Failed to update requirement'
            )
          }
        />
      )
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_: unknown, row: DocumentRequirementAdminRow) => (
        <Popconfirm
          title='Delete this requirement?'
          description='It disappears from every deal Documents tab immediately.'
          okText='Delete'
          okButtonProps={{ danger: true }}
          onConfirm={() =>
            run(
              () => deleteDocumentRequirement(token(), row.deal_document_requirements__id),
              'Failed to delete requirement'
            )
          }
        >
          <Button size='small' type='text' danger icon={<DeleteOutlined />} />
        </Popconfirm>
      )
    }
  ]

  const patchEditing = (fields: Partial<DocumentRequirementFields>): void =>
    setEditing((prev) => (prev ? { ...prev, ...fields } : prev))

  return (
    <div className={`${CONFIG_PANEL_SCROLL_CLASS} space-y-4`}>
      <FormSection
        title='Required Documents'
        titleColor='blue'
        scrollable={false}
        loading={loading}
        headerActions={
          <span className='flex items-center gap-2'>
            <Text type='secondary' style={{ fontSize: 12 }}>
              Blank scope = applies to every pipeline / deal type / security type / fund
            </Text>
            <Button size='small' icon={<ReloadOutlined />} onClick={load}>
              Refresh
            </Button>
            <Button size='small' type='primary' icon={<PlusOutlined />} onClick={openNewRequirement}>
              Add requirement
            </Button>
          </span>
        }
      >
        <Table
          size='small'
          rowKey='deal_document_requirements__id'
          pagination={false}
          dataSource={data.requirements}
          columns={requirementColumns}
          scroll={{ x: 1500 }}
        />
      </FormSection>

      <FormSection title='Document Types' titleColor='green' scrollable={false} loading={loading}>
        <Table
          size='small'
          rowKey='document_types__id'
          pagination={false}
          dataSource={data.types}
          columns={typeColumns}
        />
        <div className='flex gap-2 mt-3'>
          <Input
            placeholder='New document type…'
            value={newType}
            onChange={(e) => setNewType(e.target.value)}
            onPressEnter={handleAddType}
            className='max-w-[320px]'
          />
          <Select
            allowClear
            placeholder='Category'
            className='min-w-[200px]'
            value={newTypeCategory ?? undefined}
            options={categoryOptions}
            onChange={(value) => setNewTypeCategory(value ?? null)}
          />
          <Button type='primary' icon={<PlusOutlined />} onClick={handleAddType} disabled={!newType.trim()}>
            Add type
          </Button>
        </div>
      </FormSection>

      <FormSection title='Categories' titleColor='purple' scrollable={false} loading={loading}>
        <Table
          size='small'
          rowKey='document_categories__id'
          pagination={false}
          dataSource={data.categories}
          columns={categoryColumns}
        />
        <div className='flex gap-2 mt-3'>
          <Input
            placeholder='New category…'
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            onPressEnter={handleAddCategory}
            className='max-w-[320px]'
          />
          <Button
            type='primary'
            icon={<PlusOutlined />}
            onClick={handleAddCategory}
            disabled={!newCategory.trim()}
          >
            Add category
          </Button>
        </div>
      </FormSection>

      <Modal
        title={editingId ? 'Edit requirement' : 'New requirement'}
        open={editing !== null}
        onCancel={() => {
          setEditing(null)
          setEditingId(null)
        }}
        onOk={handleSaveRequirement}
        okText='Save'
        confirmLoading={saving}
        width={640}
        destroyOnClose
      >
        {editing ? (
          <div className='space-y-3'>
            <div>
              <label className='block text-xs text-gray-500 mb-1'>
                <span className='text-red-500'>*</span> Name
              </label>
              <Input
                value={editing.name}
                onChange={(e) => patchEditing({ name: e.target.value })}
                placeholder='e.g. Purchase Agreement'
              />
            </div>
            <div>
              <label className='block text-xs text-gray-500 mb-1'>
                <span className='text-red-500'>*</span> Satisfied by any of
              </label>
              <Select
                mode='multiple'
                className='w-full'
                placeholder='Document types that satisfy this requirement'
                value={editing.satisfied_by_document_type_ids}
                options={typeOptions}
                onChange={(value) => patchEditing({ satisfied_by_document_type_ids: value })}
              />
            </div>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <label className='block text-xs text-gray-500 mb-1'>Execution pipeline</label>
                <Select
                  mode='multiple'
                  allowClear
                  showSearch
                  optionFilterProp='label'
                  className='w-full'
                  placeholder={`${ANY} pipeline`}
                  value={editing.pipeline_ids ?? []}
                  options={executionPipelineOptions}
                  onChange={(value) => patchEditing({ pipeline_ids: value })}
                />
              </div>
              <div>
                <label className='block text-xs text-gray-500 mb-1'>Deal type</label>
                <Select
                  mode='multiple'
                  allowClear
                  showSearch
                  optionFilterProp='label'
                  className='w-full'
                  placeholder={`${ANY} deal type`}
                  value={editing.deal_type_ids ?? []}
                  options={dealTypeOptions}
                  onChange={(value) => patchEditing({ deal_type_ids: value })}
                />
              </div>
              <div>
                <label className='block text-xs text-gray-500 mb-1'>Security type</label>
                <Select
                  mode='multiple'
                  allowClear
                  showSearch
                  optionFilterProp='label'
                  className='w-full'
                  placeholder={`${ANY} security type`}
                  value={editing.security_type_ids ?? []}
                  options={securityTypeOptions}
                  onChange={(value) => patchEditing({ security_type_ids: value })}
                />
              </div>
              <div>
                <label className='block text-xs text-gray-500 mb-1'>Fund</label>
                <Select
                  mode='multiple'
                  allowClear
                  showSearch
                  optionFilterProp='label'
                  className='w-full'
                  placeholder={`${ANY} fund`}
                  value={editing.fund_ids ?? []}
                  options={fundOptions}
                  onChange={(value) => patchEditing({ fund_ids: value })}
                />
              </div>
              <div>
                <label className='block text-xs text-gray-500 mb-1'>Required lifecycle</label>
                <Select
                  className='w-full'
                  value={editing.required_lifecycle}
                  options={LIFECYCLE_OPTIONS}
                  onChange={(value) => patchEditing({ required_lifecycle: value })}
                />
              </div>
              <div>
                <label className='block text-xs text-gray-500 mb-1'>Sequence</label>
                <InputNumber
                  className='w-full'
                  min={0}
                  value={editing.sequence ?? 0}
                  onChange={(value) => patchEditing({ sequence: (value as number) ?? 0 })}
                />
              </div>
            </div>
            <div className='flex items-center gap-2'>
              <Switch
                size='small'
                checked={editing.active !== false}
                onChange={(checked) => patchEditing({ active: checked })}
              />
              <span className='text-xs text-gray-500'>
                Active — inactive rules are hidden from every deal Documents tab
              </span>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  )
}
